import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';

interface Opcao {
  letra: string;
  texto: string;
  correta: boolean;
  ordem: number;
}

interface CreateQuestaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (questaoData: any) => void;
  questaoEditando?: any;
  proximaOrdem: number;
}

const CreateQuestaoModal: React.FC<CreateQuestaoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  questaoEditando,
  proximaOrdem
}) => {
  const [formData, setFormData] = useState({
    ordem: proximaOrdem,
    tipo: 'multipla_escolha' as 'multipla_escolha' | 'texto_livre',
    pontuacao: 1,
    enunciado: '',
    resposta_texto_correta: '',
    explicacao: '',
    opcoes: [
      { letra: 'A', texto: '', correta: false, ordem: 1 },
      { letra: 'B', texto: '', correta: false, ordem: 2 }
    ] as Opcao[]
  });

  useEffect(() => {
    if (questaoEditando) {
      // As opções podem vir em opcoes ou __opcoes__
      const opcoes = questaoEditando.opcoes || questaoEditando.__opcoes__ || [];
      const opcoesFormatadas = opcoes.map((opcao: any) => ({
        letra: opcao.letra,
        texto: opcao.texto,
        correta: opcao.correta,
        ordem: opcao.ordem
      }));

      setFormData({
        ordem: questaoEditando.ordem,
        tipo: questaoEditando.tipo,
        pontuacao: questaoEditando.pontuacao,
        enunciado: questaoEditando.enunciado,
        resposta_texto_correta: questaoEditando.resposta_texto_correta || '',
        explicacao: questaoEditando.explicacao || '',
        opcoes: opcoesFormatadas.length > 0 ? opcoesFormatadas : [
          { letra: 'A', texto: '', correta: false, ordem: 1 },
          { letra: 'B', texto: '', correta: false, ordem: 2 }
        ]
      });
    } else {
      setFormData({
        ordem: proximaOrdem,
        tipo: 'multipla_escolha',
        pontuacao: 1,
        enunciado: '',
        resposta_texto_correta: '',
        explicacao: '',
        opcoes: [
          { letra: 'A', texto: '', correta: false, ordem: 1 },
          { letra: 'B', texto: '', correta: false, ordem: 2 }
        ]
      });
    }
  }, [questaoEditando, proximaOrdem]);

  const adicionarOpcao = () => {
    const letras = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    const proximaLetra = letras[formData.opcoes.length];

    if (proximaLetra) {
      setFormData(prev => ({
        ...prev,
        opcoes: [...prev.opcoes, {
          letra: proximaLetra,
          texto: '',
          correta: false,
          ordem: prev.opcoes.length + 1
        }]
      }));
    }
  };

  const removerOpcao = (index: number) => {
    if (formData.opcoes.length > 2) {
      setFormData(prev => ({
        ...prev,
        opcoes: prev.opcoes.filter((_, i) => i !== index)
      }));
    }
  };

  const atualizarOpcao = (index: number, campo: string, valor: any) => {
    setFormData(prev => ({
      ...prev,
      opcoes: prev.opcoes.map((opcao, i) => {
        if (i === index) {
          if (campo === 'correta' && valor) {
            // Se está marcando como correta, desmarcar todas as outras
            return { ...opcao, [campo]: valor };
          }
          return { ...opcao, [campo]: valor };
        } else if (campo === 'correta' && valor) {
          // Desmarcar outras opções quando uma é marcada como correta
          return { ...opcao, correta: false };
        }
        return opcao;
      })
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.enunciado.trim()) {
      enqueueSnackbar('O enunciado é obrigatório', { variant: 'warning' });
      return;
    }

    if (formData.tipo === 'multipla_escolha') {
      // Verificar se todas as opções estão preenchidas
      const opcoesVazias = formData.opcoes.some(opcao => !opcao.texto.trim());
      if (opcoesVazias) {
        enqueueSnackbar('Todas as opções devem ser preenchidas', { variant: 'warning' });
        return;
      }

      // Verificar se pelo menos uma opção está marcada como correta
      const temCorreta = formData.opcoes.some(opcao => opcao.correta);
      if (!temCorreta) {
        enqueueSnackbar('Pelo menos uma opção deve estar marcada como correta', { variant: 'warning' });
        return;
      }
    } else if (formData.tipo === 'texto_livre') {
      if (!formData.resposta_texto_correta.trim()) {
        enqueueSnackbar('A resposta esperada é obrigatória para questões de texto livre', { variant: 'warning' });
        return;
      }
    }

    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold">
            {questaoEditando ? 'Editar Questão' : 'Nova Questão'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Ordem
              </label>
              <input
                type="number"
                min="1"
                value={formData.ordem}
                onChange={(e) => setFormData(prev => ({ ...prev, ordem: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Tipo
              </label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  tipo: e.target.value as 'multipla_escolha' | 'texto_livre'
                }))}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="multipla_escolha">Múltipla Escolha</option>
                <option value="texto_livre">Texto Livre</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Pontuação
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={formData.pontuacao}
                onChange={(e) => setFormData(prev => ({ ...prev, pontuacao: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Enunciado *
            </label>
            <textarea
              value={formData.enunciado}
              onChange={(e) => setFormData(prev => ({ ...prev, enunciado: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Digite o enunciado da questão..."
              required
            />
          </div>

          {formData.tipo === 'texto_livre' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Resposta Esperada *
              </label>
              <textarea
                value={formData.resposta_texto_correta}
                onChange={(e) => setFormData(prev => ({ ...prev, resposta_texto_correta: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Digite a resposta esperada..."
                required
              />
            </div>
          )}

          {formData.tipo === 'multipla_escolha' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-foreground">
                  Opções de Resposta
                </label>
                <button
                  type="button"
                  onClick={adicionarOpcao}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  <Plus size={16} />
                  Adicionar Opção
                </button>
              </div>

              <div className="space-y-3">
                {formData.opcoes.map((opcao, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border border-border rounded-md bg-background">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="opcao_correta"
                        checked={opcao.correta}
                        onChange={(e) => atualizarOpcao(index, 'correta', e.target.checked)}
                        className="w-4 h-4 text-blue-600"
                      />
                    </div>
                    <div className="flex-shrink-0 w-8 text-center font-medium">
                      {opcao.letra}
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={opcao.texto}
                        onChange={(e) => atualizarOpcao(index, 'texto', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder={`Opção ${opcao.letra}...`}
                      />
                    </div>
                    {formData.opcoes.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removerOpcao(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Explicação (opcional)
            </label>
            <textarea
              value={formData.explicacao}
              onChange={(e) => setFormData(prev => ({ ...prev, explicacao: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Explicação da resposta correta..."
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-foreground border border-border rounded-md hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              {questaoEditando ? 'Atualizar' : 'Criar'} Questão
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateQuestaoModal;
